import create from 'zustand';
import { devtools } from 'zustand/middleware';
import api from '../lib/api';
import Auth from '../lib/Auth';

const authStore = (set, get) => ({
  user: null,
  setUser: (user) => {
    set({ user: user });
    Auth.currentUser = user;
  },

  authenticated: false,
  checkingAuth: false,
  getUserLoading: false,
  otpEnabled: false,
  checkAuthentication: () => {
    set({ checkingAuth: true });
    const { logout } = get();
    const hasToken = !!Auth.accessToken;
    const user = Auth.currentUser;

    if (hasToken && user) {
      set({
        user: user,
        authenticated: true,
        checkingAuth: false,
      });
    } else {
      set({ checkingAuth: false });
      logout();
    }
  },

  checkOtpEnabled: () => {
    const otpEnabled = Auth.currentUser?.twofactor?.otp_2fa_enabled || false;
    set({ otpEnabled: otpEnabled });
  },

  login: async (email, password) => {
    try {
      let res;
      try {
        res = await api.post('/auth/login/', {
          email: email,
          password: password,
        });
      } catch (apiError) {
        console.error('❌ Login API call failed:', {
          status: apiError.response?.status,
          message: apiError.message,
        });
        throw apiError;
      }

      // Handle different Django response formats
      // Check for token in various possible fields: access_token, access, or key
      const accessToken =
        res.data.access_token || res.data.access || res.data.key;
      const refreshToken =
        res.data.refresh_token || res.data.refresh || res.data.refreshToken;

      if (!accessToken) {
        console.error(
          '❌ No access token in response! Available keys:',
          Object.keys(res.data || {})
        );
        throw new Error('No access token received from server');
      }

      // Refresh token is optional - some backends don't return it on login
      Auth.accessToken = accessToken;
      if (refreshToken) {
        Auth.refreshToken = refreshToken;
      }

      // Try to get user from response, or fetch it separately
      let user = res.data.user;
      if (!user) {
        try {
          const { getUserInformation } = get();
          await getUserInformation();
          user = Auth.currentUser;
        } catch (userError) {
          console.warn('⚠️ Could not fetch user information:', userError);
          // Continue without user - it might be fetched later
        }
      } else {
        Auth.currentUser = user;
      }

      // Verify storage
      const verifyAccessToken = localStorage.getItem('ACCESS_TOKEN');
      if (!verifyAccessToken) {
        console.error('❌ CRITICAL: Access token storage verification failed!');
        throw new Error('Failed to store access token');
      }

      set({ user: user, authenticated: true, checkingAuth: false });
    } catch (e) {
      console.error('❌ Login error:', e);
      set({ checkingAuth: false });
      throw new Error('Invalid username or password');
    }
  },

  // General logout method that clears tokens and user data
  logout: () => {
    set({ user: null, authenticated: false, checkingAuth: false });
    Auth.logout(); // General logout, clears tokens and user data
  },

  byPassLogin: (token, refreshToken) => {
    Auth.accessToken = token;
    Auth.refreshToken = refreshToken;
  },

  getUserInformation: async () => {
    set({ getUserLoading: true });
    let res = await api.get('/api/users/me');
    Auth.currentUser = res.data;
    set({ user: res.data, authenticated: true, getUserLoading: false });
  },
});

let useAuthStore;
if (process.env.NODE_ENV !== 'production') {
  useAuthStore = create(devtools(authStore));
} else {
  useAuthStore = create(authStore);
}

export default useAuthStore;
